import { useMemo } from "react";
import { Badge } from "@/components/base/badges/badges";

/**
 * UC-006 R-040 / F11 — Card invalidation 48h SLA countdown badge (Phase 28).
 *
 * Color-not-only signaling (REQ-UI-022):
 *   - tier "ok"        — > 24 h remaining → green   + ikona ✓
 *   - tier "warn"      — 12-24 h          → yellow  + ikona ⚠
 *   - tier "urgent"    — < 12 h           → red     + ikona ⏰
 *   - tier "breach"    — past deadline    — critical red + ikona ⚠⚠ (SLA breach)
 *
 * Color-not-only means: text label ("Zbývá Xh / Y dní" / "Termín překročen o Xh") +
 * ikona char + aria-label se pošle do screen readeru s explicit tier text.
 *
 * Props `flaggedAt` — ISO timestamp odkdy se 48h odpočítává.
 * Optional `now` pro deterministic test rendering.
 */

export type CountdownTier = "ok" | "warn" | "urgent" | "breach";

export interface CardInvalidationCountdownBadgeProps {
  flaggedAt: string;
  now?: Date;
  className?: string;
}

const TIER_ICON: Record<CountdownTier, string> = {
  ok: "✓",
  warn: "⚠",
  urgent: "⏰",
  breach: "⚠⚠",
};

const TIER_COLOR: Record<CountdownTier, "success" | "warning" | "error"> = {
  ok: "success",
  warn: "warning",
  urgent: "error",
  breach: "error",
};

const TIER_LABEL: Record<CountdownTier, string> = {
  ok: "OK",
  warn: "Pozor",
  urgent: "Naléhavé",
  breach: "Porušeno",
};

const SLA_DURATION_MS = 48 * 60 * 60 * 1000;

export function computeCountdown(
  flaggedAt: string,
  now: Date = new Date(),
): {
  tier: CountdownTier;
  remainingMs: number;
  label: string;
  ariaLabel: string;
} {
  const flagged = Date.parse(flaggedAt);
  if (Number.isNaN(flagged)) {
    return {
      tier: "warn",
      remainingMs: 0,
      label: "Neznámé",
      ariaLabel: "Neznámé deadline",
    };
  }
  const deadline = flagged + SLA_DURATION_MS;
  const remainingMs = deadline - now.getTime();

  if (remainingMs <= 0) {
    const overdueMs = Math.abs(remainingMs);
    const overdueHours = Math.floor(overdueMs / 3_600_000);
    const overdueDays = Math.floor(overdueHours / 24);
    const label =
      overdueDays >= 1
        ? `Termín překročen (${overdueDays} d ${overdueHours % 24} h)`
        : `Termín překročen (${overdueHours} h)`;
    return {
      tier: "breach",
      remainingMs,
      label,
      ariaLabel: `Termín překročen: ${TIER_LABEL.breach} — ${label}`,
    };
  }

  const remainingHours = Math.floor(remainingMs / 3_600_000);
  const remainingDays = Math.floor(remainingHours / 24);
  const formatted =
    remainingDays >= 1
      ? `Zbývá ${remainingDays} d ${remainingHours % 24} h`
      : `Zbývá ${remainingHours} h`;

  let tier: CountdownTier;
  if (remainingHours < 12) {
    tier = "urgent";
  } else if (remainingHours < 24) {
    tier = "warn";
  } else {
    tier = "ok";
  }

  return {
    tier,
    remainingMs,
    label: formatted,
    ariaLabel: `${TIER_LABEL[tier]}: ${formatted}`,
  };
}

export function CardInvalidationCountdownBadge({
  flaggedAt,
  now,
  className,
}: CardInvalidationCountdownBadgeProps) {
  const result = useMemo(
    () => computeCountdown(flaggedAt, now),
    [flaggedAt, now],
  );

  // Native Badge nepropouští aria-label/data-* props → obal do <span>.
  return (
    <span
      aria-label={result.ariaLabel}
      data-tier={result.tier}
      data-testid="card-invalidation-countdown"
    >
      <Badge color={TIER_COLOR[result.tier]} className={className}>
        <span aria-hidden="true">{TIER_ICON[result.tier]}</span>{" "}
        <span>{result.label}</span>
      </Badge>
    </span>
  );
}
